import { css } from '@microsoft/fast-element';
import { neutralForegroundActive, neutralForegroundRest, neutralInputFillHover, neutralInputFillRest, neutralLayer1, neutralStealthFillActive, neutralStealthFillHover, neutralStealthFillRest, neutralStrokeFocus, neutralStrokeRest, getToken, neutralDivider } from '../../design-tokens';
import { appearanceBehavior } from '../../styles/behaviors';
import { elevationRule } from '../../styles/elevation';
import { heightNumber, paddingNumber } from '../../styles/size';
import { display } from '../../foundation/display';

export const selectStealthStyles = css`
        :host .control {
            background: ${neutralStealthFillRest};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):hover) .control {
            background: ${neutralStealthFillHover};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):active) .control {
            background: ${neutralStealthFillActive};
            border-color: transparent;
            box-shadow: none;
        }
    `;

export const selectLightweightStyles = css`
        :host .control {
            background: transparent;
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):hover) .control {
            background: ${neutralStealthFillHover};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):active) .control {
            background: ${neutralStealthFillActive};
            border-color: transparent;
            box-shadow: none;
        }
    `;

export const selectStyles = css`
    ${display("inline-flex")} :host {
        --elevation: ${getToken('space-high-elevation')};
        --listbox-max-height: 6;
        position: relative;
        box-sizing: border-box;

        font-family: ${getToken('typography-font-family')};
        fill: currentcolor;

        user-select: none;
        outline: none;
    }

    :host .listbox {
        ${elevationRule}
        background: ${neutralLayer1};
        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        box-sizing: border-box;
        display: inline-flex;
        flex-direction: column;
        left: 0;
        max-height: calc((var(--listbox-max-height) * ${heightNumber} + ${getToken('space-stroke-width')}) * 1px);
        padding: 0;
        overflow-y: auto;
        position: absolute;
        width: 100%;
        z-index: 1;
    }

    :host .listbox[hidden] {
        display: none;
    }

    :host .control {
        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        height: calc(${heightNumber} * 1px);
        vertical-align: top;
        background: ${neutralInputFillRest};
        color: ${neutralForegroundRest};
        box-shadow: 0 0 calc(${getToken('space-input-shadow')} * 1px) 0 rgba(0, 0, 0, 0.1);
        align-items: center;
        box-sizing: border-box;
        cursor: pointer;
        display: flex;
        font-size: ${getToken('typography-control-font-size')};
        font-family: inherit;
        line-height: ${getToken('typography-control-line-height')};
        padding-left: calc(${paddingNumber} * 1px);
        width: 100%;
    }

    :host(:not([disabled]):hover) .control {
        background: ${neutralInputFillHover};
        border-color: ${getToken('color-primary-hover')};
    }

    :host(:focus-visible) .control {
        border-color: ${neutralStrokeFocus};
        box-shadow: 0 0 0 calc(${getToken('space-focus-stroke-width')} * 1px) ${neutralStrokeFocus};
    }

    :host([disabled]) {
        cursor: not-allowed;
        opacity: ${getToken('color-disabled-opacity')};
    }

    :host([disabled]) .control {
        cursor: not-allowed;
        user-select: none;
    }

    :host([open]) .control {
        color: ${neutralForegroundActive};
    }

    :host([open][position="above"]) .listbox {
        bottom: calc((${heightNumber} + ${getToken('space-design-unit')}) * 1px);
    }

    :host([open][position="below"]) .listbox {
        top: calc((${heightNumber} + ${getToken('space-design-unit')}) * 1px);
    }

    .selected-value {
        flex: 1 1 auto;
        font-family: inherit;
        text-align: start;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .indicator {
        display: flex;
        justify-content: center;
        flex: 0 0 auto;
        width: calc(${heightNumber} * 1px);
    }

    slot[name="listbox"] {
        display: none;
        width: 100%;
    }

    :host([open]) slot[name="listbox"] {
        display: flex;
        position: absolute;
        ${elevationRule}
    }

    .end {
        margin-inline-start: auto;
    }

    ::slotted(svg),
    slot > svg {
        display: flex;
        height: 1em;
        width: 1em;
    }

    ::slotted([role="option"]) {
        flex: 0 0 auto;
    }

    ::slotted([role="option"]:not(:last-child)) {
        border-bottom: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralDivider} !important;
    }
`.withBehaviors(
    appearanceBehavior("stealth", selectStealthStyles),
    appearanceBehavior("lightweight", selectLightweightStyles)
);
